import React, { useState, useEffect, CSSProperties } from 'react';

interface EventCountdownProps {
  targetDate: string | Date;
  meetingUrl: string;
  title?: string;
  description?: string;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalMs: number;
}

const EventCountdown: React.FC<EventCountdownProps> = ({ 
  targetDate, 
  meetingUrl,
  title = "Web Conference", 
  description 
}) => {
  
  const [isRedirecting, setIsRedirecting] = useState(false);
  const PRE_JOIN_WINDOW = 30 * 60 * 1000; // 30 minutes

  const calculateTimeLeft = (): TimeLeft => {
    const difference = +new Date(targetDate) - +new Date();
    
    // Prevent negative numbers in display
    if (difference < 0) {
       return { days: 0, hours: 0, minutes: 0, seconds: 0, totalMs: difference };
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
      totalMs: difference,
    };
  };

  const [timeLeft, setTimeLeft] = useState<TimeLeft>(calculateTimeLeft());

  useEffect(() => {
    // Initial check
    const initialCheck = calculateTimeLeft();
    if (initialCheck.totalMs <= PRE_JOIN_WINDOW && initialCheck.totalMs > -3600000 && !isRedirecting) {
        // Only redirect if within window AND not way in the past (e.g. 1 hour ago)
        handleRedirect();
        return;
    }

    const timer = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);

      if (remaining.totalMs <= PRE_JOIN_WINDOW && remaining.totalMs > 0 && !isRedirecting) {
        handleRedirect();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate, isRedirecting]);

  const handleRedirect = () => {
    setIsRedirecting(true);
    setTimeout(() => {
        window.location.href = meetingUrl;
    }, 2500); 
  };

  const goBack = () => {
    if (window.history.length > 1) {
        window.history.back();
    }
  };

  const goHome = () => {
    window.location.href = '/home';
  };

  const formatNumber = (num: number): string => {
    return num < 10 ? `0${num}` : num.toString();
  };

  if (isRedirecting) {
    return (
        <div style={styles.container}>
            <div style={styles.loadingWrapper}>
                <h2 style={styles.header}>Meeting Window Open</h2>
                <p style={styles.subHeader}>Redirecting you to the lobby...</p>
                <div style={styles.spinner}></div>
            </div>
        </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.header}>{title}</h2>
      
      {description && (
          <p style={styles.description}>{description}</p>
      )}

      <div style={styles.timerWrapper}>
        <div style={styles.timeCard}>
          <span style={styles.number}>{formatNumber(timeLeft.days)}</span>
          <span style={styles.label}>Days</span>
        </div>

        <div style={styles.separator}>:</div>

        <div style={styles.timeCard}>
          <span style={styles.number}>{formatNumber(timeLeft.hours)}</span>
          <span style={styles.label}>Hours</span>
        </div>

        <div style={styles.separator}>:</div>

        <div style={styles.timeCard}>
          <span style={styles.number}>{formatNumber(timeLeft.minutes)}</span>
          <span style={styles.label}>Minutes</span>
        </div>

        <div style={styles.separator}>:</div>

        <div style={styles.timeCard}>
          <span style={styles.number}>{formatNumber(timeLeft.seconds)}</span>
          <span style={styles.label}>Seconds</span>
        </div>
      </div>

      <div style={styles.infoBox}>
        <span style={{ fontSize: '1.4rem' }}>ℹ️</span>
        <div>
            <strong>Lobby opens 30 minutes prior.</strong>
            <br />
            <span style={{ opacity: 0.9, fontSize: '0.9rem' }}>
                You will be automatically redirected when the meeting is ready.
            </span>
        </div>
      </div>

      <div style={styles.buttonRow}>
        <button onClick={goBack} style={styles.buttonOutline}>
            &larr; Back
        </button>
        <button onClick={goHome} style={styles.buttonSolid}>
            Home
        </button>
      </div>
    </div>
  );
};

// --- Styles ---
const styles: { [key: string]: CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    // Solid gradient background
    background: 'linear-gradient(135deg, #7E57C2 0%, #6200ea 100%)',
    padding: '3rem 2rem',
    borderRadius: '16px',
    boxShadow: '0 10px 30px rgba(81, 45, 168, 0.4)',
    color: '#ffffff',
    fontFamily: '"Segoe UI", Roboto, sans-serif',
    maxWidth: '700px',
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
    textAlign: 'center',
  },
  header: {
    margin: '0 0 0.5rem 0',
    color: '#ffffff',
    fontSize: '2rem',
    fontWeight: 700,
    textShadow: '0 2px 4px rgba(0,0,0,0.2)', // Helps text pop off background
  },
  subHeader: {
    fontSize: '1.1rem', 
    color: '#ffffffc9',
    opacity: 0.9 
  },
  description: {
    margin: '0 0 2.5rem 0',
    fontSize: '1.1rem',
    color: '#ffffffc9',
    lineHeight: 1.6,
    maxWidth: '550px',
    opacity: 0.95, // High opacity for readability
  },
  timerWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginBottom: '2.5rem',
  },
  timeCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    // HIGH CONTRAST CHANGE: Solid white card
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    minWidth: '90px',
    padding: '1rem 0.5rem',
    // Deep purple text on white background = Excellent contrast
    color: '#7E57C2', 
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
  },
  number: {
    fontSize: '2.8rem',
    fontWeight: 800,
    lineHeight: 1,
    marginBottom: '0.2rem',
    fontVariantNumeric: 'tabular-nums', // Keeps numbers from jumping width
  },
  label: {
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    fontWeight: 600,
    letterSpacing: '1px',
    color: '#5e35b1', // Slightly darker purple for the small text
  },
  separator: {
    fontSize: '2.5rem',
    fontWeight: 700,
    marginTop: '-15px',
    color: '#ffffff', // Solid white against purple background
    opacity: 1, // Full opacity
  },
  infoBox: {
    // Darker semi-transparent background to ensure white text is readable
    backgroundColor: 'rgba(0, 0, 0, 0.25)', 
    border: '1px solid rgba(255, 255, 255, 0.2)',
    padding: '1rem 1.5rem',
    borderRadius: '12px',
    marginBottom: '2rem',
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    textAlign: 'left',
    maxWidth: '500px',
  },
  buttonRow: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
  },
  buttonOutline: {
    padding: '0.8rem 2rem',
    background: 'transparent',
    border: '2px solid #ffffff',
    color: '#ffffff',
    borderRadius: '50px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 600,
    transition: 'background 0.2s',
  },
  buttonSolid: {
    padding: '0.8rem 2rem',
    background: '#ffffff',
    border: '2px solid #ffffff',
    color: '#7E57C2',
    borderRadius: '50px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 700,
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  },
  loadingWrapper: {
    padding: '4rem 0',
  },
  spinner: {
    display: 'inline-block',
    width: '50px',
    height: '50px',
    border: '5px solid rgba(255,255,255,0.3)',
    borderRadius: '50%',
    borderTopColor: '#fff',
    animation: 'spin 1s ease-in-out infinite',
    marginTop: '1.5rem',
  }
};

export default EventCountdown;